// frontend/modules/notification-config/hooks/useNotificationConfigs.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { 
  useGetAllNotificationConfigurations,
  useGetNotificationConfigurationById,
  useGetNotificationConfigurationByCode,
  useCreateNotificationConfiguration,
  useUpdateNotificationConfiguration,
  useDeleteNotificationConfiguration,
  searchNotificationConfigurations,
  getNotificationConfigurationByCode,
  useExecuteNotificationConfiguration,
  useTestNotificationConfigurationComplete,
  useValidateConfiguration,
  useTestContextTransformation,
  useTestTemplateProcessing,
  useTestRecipientResolution,
  exportConfigurations,
  importConfigurations
} from '@/shared/api/client/notification-configuration-controller';
import { useNotificationConfigStore } from '../stores/notification-config-store';
import { 
  NotificationConfig
} from '../types/notification-config.types';
import { 
  NotificationConfigurationDTO, 
  NotificationExecutionRequest,
  TemplateTestRequest,
  NotificationTestRequest
} from '@/shared/api/model';
import { useGetAllScripts } from '@/shared/api/client/context-script-controller';

export const useNotificationConfigs = () => {
  const queryClient = useQueryClient();
  const [importResult, setImportResult] = useState<any>(null);
  
  const { 
    configurations, 
    filters, 
    selectedConfiguration,
    isEditModalVisible,
    isTestModalVisible,
    setConfigurations, 
    setSelectedConfiguration,
    setFilters,
    clearFilters,
    setEditModalVisible,
    setTestModalVisible
  } = useNotificationConfigStore();

  // Fetch configurations with optional active filter
  const { data: configsResponse, isLoading } = useGetAllNotificationConfigurations({
    activeOnly: filters.active
  });

  // Fetch all context scripts to populate dropdowns
  const { data: scriptsResponse } = useGetAllScripts({});

  // Effect to update configurations when data changes
  useEffect(() => {
    if (configsResponse) {
      // Enhance configurations with script names
      const scripts = scriptsResponse || [];
      const enhancedConfigs = configsResponse.map(config => {
        const scriptName = scripts.find(s => s.id === config.contextScriptId)?.name || '';
        return {
          ...config,
          contextScriptName: scriptName
        } as NotificationConfig;
      });
      
      setConfigurations(enhancedConfigs);
    }
  }, [configsResponse, scriptsResponse, setConfigurations]);

  // Create configuration mutation
  const { mutateAsync: createConfig } = useCreateNotificationConfiguration({
    mutation: {
      onSuccess: () => {
        message.success('Notification configuration created successfully');
        queryClient.invalidateQueries({ queryKey: ['/api/notification-configurations'] });
        setEditModalVisible(false);
      },
      onError: (err) => {
        console.error('Error creating notification configuration:', err);
        message.error('Failed to create notification configuration');
      }
    }
  });

  // Update configuration mutation
  const { mutateAsync: updateConfig } = useUpdateNotificationConfiguration({
    mutation: {
      onSuccess: () => {
        message.success('Notification configuration updated successfully');
        queryClient.invalidateQueries({ queryKey: ['/api/notification-configurations'] });
        setEditModalVisible(false);
      },
      onError: (err) => {
        console.error('Error updating notification configuration:', err);
        message.error('Failed to update notification configuration');
      }
    }
  });

  // Delete configuration mutation
  const { mutateAsync: deleteConfig } = useDeleteNotificationConfiguration({
    mutation: {
      onSuccess: () => {
        message.success('Notification configuration deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['/api/notification-configurations'] });
      },
      onError: (err) => {
        console.error('Error deleting notification configuration:', err);
        message.error('Failed to delete notification configuration');
      }
    }
  });

  // Execute configuration mutation
  const { mutateAsync: executeConfig } = useExecuteNotificationConfiguration({
    mutation: {
      onError: (err) => {
        console.error('Error executing notification configuration:', err);
        message.error('Failed to execute notification configuration');
      }
    }
  });

  // Test configuration complete flow mutation
  const { mutateAsync: testConfig } = useTestNotificationConfigurationComplete({
    mutation: {
      onError: (err) => {
        console.error('Error testing notification configuration:', err);
        message.error('Failed to test notification configuration');
      }
    }
  });

  // Validate configuration mutation
  const { mutateAsync: validateConfig } = useValidateConfiguration({
    mutation: {
      onError: (err) => {
        console.error('Error validating configuration:', err);
        message.error('Failed to validate configuration');
      }
    }
  });

  // Test context transformation mutation
  const { mutateAsync: testTransformation } = useTestContextTransformation({
    mutation: {
      onError: (err) => {
        console.error('Error testing context transformation:', err);
        message.error('Failed to test context transformation');
      }
    }
  });

  // Test template processing mutation
  const { mutateAsync: testTemplate } = useTestTemplateProcessing({
    mutation: {
      onError: (err) => {
        console.error('Error testing template processing:', err);
        message.error('Failed to test template processing');
      }
    }
  });

  // Test recipient resolution mutation
  const { mutateAsync: testRecipients } = useTestRecipientResolution({
    mutation: {
      onError: (err) => {
        console.error('Error testing recipient resolution:', err);
        message.error('Failed to test recipient resolution');
      }
    }
  });

  // Search configurations
  const searchConfigurations = useCallback(async (query: string) => {
    try {
      const response = await searchNotificationConfigurations({ query });
      return response;
    } catch (err) {
      console.error('Error searching configurations:', err);
      message.error('Failed to search configurations');
      return [];
    }
  }, []);

  // Get configuration by code
  const getConfigurationByCode = useCallback(async (code: string) => {
    try {
      const response = await getNotificationConfigurationByCode(code);
      return response;
    } catch (err) {
      console.error(`Error getting configuration with code ${code}:`, err);
      message.error(`Failed to get configuration with code ${code}`);
      return null;
    }
  }, []);

  const refetchConfigurations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['/api/notification-configurations'] });
  }, [queryClient]);

  // Computed values
  const filteredConfigurations = useMemo(() => {
    return configurations.filter(config => {
      if (filters.platform && config.platform !== filters.platform) return false;
      if (filters.active !== undefined && config.active !== filters.active) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        return (
          config.code.toLowerCase().includes(search) ||
          config.name.toLowerCase().includes(search) ||
          (config.description && config.description.toLowerCase().includes(search))
        );
      }
      return true;
    });
  }, [configurations, filters]);

  // Helper function to adapt local NotificationTestRequest to backend NotificationTestRequest
  const adaptTestRequest = (request: any): NotificationTestRequest => {
    return {
      configuration: request.configuration,
      executionRequest: {
        contextJson: request.contextJson,
        language: request.language
      }
    };
  };

  // Export configurations
  const exportConfigs = useCallback(async () => {
    try {
      const response = await exportConfigurations();
      const configurations = response;
      
      // Create and download JSON file
      const configurationsJson = JSON.stringify(configurations, null, 2);
      const blob = new Blob([configurationsJson], { type: 'application/json' });
      const href = URL.createObjectURL(blob);

      // Create download link and trigger click
      const link = document.createElement('a');
      link.href = href;
      link.download = `notification_configurations_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      message.success('Configurations exported successfully');
    } catch (err) {
      console.error('Error exporting configurations:', err);
      message.error('Failed to export configurations');
    }
  }, []);

  // Import configurations from file
  const importConfigsFromFile = useCallback(async (file: File) => {
    try {
      // Read file contents
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          // Parse JSON
          const content = e.target?.result as string;
          const importedConfigs = JSON.parse(content);

          // Validate imported data
          if (!Array.isArray(importedConfigs)) {
            throw new Error('Invalid import file format. Expected an array of configurations.');
          }

          // Import configurations
          const response = await importConfigurations({
            // @ts-ignore
            data: importedConfigs,
            params: { overwriteExisting: false }
          });
          
          setImportResult(response);
          
          // Refresh configurations list
          await refetchConfigurations();

          const result = response;
          // @ts-ignore
          if (result.errorCount === 0) {
            message.success(`Successfully imported ${result.importedCount} configurations`);
          } else {
            message.warning(`Import completed with ${result.errorCount} errors. Check details for more information.`);
          }

        } catch (parseError) {
          console.error('Error parsing import file:', parseError);
          message.error('Invalid JSON file format');
          setImportResult({
            importedCount: 0,
            skippedCount: 0,
            errorCount: 1,
            errors: ['Invalid JSON file format'],
            warnings: [],
            totalProcessed: 0
          });
        }
      };

      reader.onerror = () => {
        message.error('Error reading file');
      };

      reader.readAsText(file);
    } catch (err) {
      console.error('Error importing configurations:', err);
      message.error('Failed to import configurations');
    }
  }, [refetchConfigurations]);

  return {
    configurations,
    filteredConfigurations,
    isLoading,
    selectedConfiguration,
    filters,
    isEditModalVisible,
    isTestModalVisible,
    setSelectedConfiguration,
    setFilters,
    clearFilters,
    setEditModalVisible,
    setTestModalVisible,
    createConfig: (config: NotificationConfigurationDTO) => createConfig({ data: config }),
    updateConfig: (config: NotificationConfigurationDTO) => updateConfig({ id: config.id || '', data: config }),
    deleteConfig: (id: string) => deleteConfig({ id }),
    executeConfig: (code: string, request: NotificationExecutionRequest) => executeConfig({ code, data: request }),
    testConfig: (request: any) => testConfig({ data: adaptTestRequest(request) }),
    validateConfig: (config: NotificationConfigurationDTO) => validateConfig({ data: config }),
    testTransformation: (request: any) => testTransformation({ data: adaptTestRequest(request) }),
    testTemplate: (request: TemplateTestRequest) => testTemplate({ data: request }),
    testRecipients: (transformedContext: Record<string, any>) => testRecipients({ data: transformedContext }),
    searchConfigurations,
    getConfigurationByCode,
    refetchConfigurations,
    exportConfigs,
    importConfigsFromFile,
    importResult
  };
};
